// recurring-donation-actions — imperative recurring lifecycle UX (RDM-R08).
//
// Surface:
//   - `runRecurringDonationAction(action, record)` — imperative call from the
//     generated recurring-donation-detail.ts `actions` override-block onClick.
//     pause / resume / reconcile fire the mutation immediately; cancel opens a
//     confirm dialog (it's destructive + irreversible).
//   - `<RecurringDonationActionsHost />` — mount once at the App root. Owns the
//     react-query mutations + invalidation and renders the confirm dialog.
//
// Why imperative instead of hooks in the config: the generated detail
// `actions: (record) => PageHeaderAction[]` callback runs during DetailPage
// render and cannot call hooks. Same single-host + module-emitter pattern as
// widget-instance-actions.tsx / bulk-tag-dialog.tsx. RBAC gating stays in the
// config (per-verb permissionGate); the BE re-enforces the matching
// recurring-donation.* permission on every route (403 → v2-api-forbidden
// interceptor).
//
// Endpoints (all real, from src/api/app/routers/recurring_donations.py):
//   POST /recurring-donations/{id}/pause      — recurring-donation.pause
//   POST /recurring-donations/{id}/resume     — recurring-donation.resume
//   POST /recurring-donations/{id}/cancel     — recurring-donation.cancel
//   POST /recurring-donations/{id}/reconcile  — recurring-donation.update

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { recurringDonationsApi } from "../api/recurring-donations";
import type { RecurringDonation } from "../api/types";
import { FormDialog } from "../components/composition/FormDialog";

export type RecurringLifecycleAction = "pause" | "resume" | "cancel" | "reconcile";

type RecurringActionRequest =
  // pause / resume / reconcile — fire immediately, no dialog.
  | { kind: "immediate"; action: Exclude<RecurringLifecycleAction, "cancel">; record: RecurringDonation }
  // cancel — destructive, requires confirmation.
  | { kind: "cancel"; record: RecurringDonation };

// ─────────────────────────────────────────────────────────────────
// Emitter: a singleton the host subscribes to. Mount exactly one host.
// ─────────────────────────────────────────────────────────────────

type Listener = (req: RecurringActionRequest) => void;
let activeListener: Listener | null = null;

export function runRecurringDonationAction(
  action: RecurringLifecycleAction,
  record: RecurringDonation,
): void {
  if (!activeListener) return; // no-op if no host mounted (dev foot-gun guard)
  if (action === "cancel") {
    activeListener({ kind: "cancel", record });
  } else {
    activeListener({ kind: "immediate", action, record });
  }
}

const MUTATION_FN: Record<
  RecurringLifecycleAction,
  (id: string) => Promise<RecurringDonation>
> = {
  pause: recurringDonationsApi.pauseRecurringDonation,
  resume: recurringDonationsApi.resumeRecurringDonation,
  cancel: recurringDonationsApi.cancelRecurringDonation,
  reconcile: recurringDonationsApi.reconcileRecurringDonation,
};

const donorLabel = (r: RecurringDonation): string =>
  r.donor_name ?? r.donor_email ?? r.donor_phone ?? "this donor";

// ─────────────────────────────────────────────────────────────────
// Host — mount once near the App root.
// ─────────────────────────────────────────────────────────────────

export function RecurringDonationActionsHost() {
  const [req, setReq] = useState<RecurringActionRequest | null>(null);

  useEffect(() => {
    activeListener = setReq;
    return () => {
      activeListener = null;
    };
  }, []);

  if (!req) return null;
  if (req.kind === "cancel") {
    return <RecurringCancelDialog record={req.record} onClose={() => setReq(null)} />;
  }
  return (
    <RecurringImmediateAction
      action={req.action}
      record={req.record}
      onDone={() => setReq(null)}
    />
  );
}

// ─────────────────────────────────────────────────────────────────
// Shared invalidation: refresh the detail record + the list.
// ─────────────────────────────────────────────────────────────────

function useInvalidateRecurring() {
  const queryClient = useQueryClient();
  return (id: string) => {
    void queryClient.invalidateQueries({ queryKey: ["recurring-donations", "detail", id] });
    void queryClient.invalidateQueries({ queryKey: ["recurring-donations", "list"] });
  };
}

// ─────────────────────────────────────────────────────────────────
// Immediate lifecycle (pause / resume / reconcile).
// ─────────────────────────────────────────────────────────────────

function RecurringImmediateAction({
  action,
  record,
  onDone,
}: {
  action: Exclude<RecurringLifecycleAction, "cancel">;
  record: RecurringDonation;
  onDone: () => void;
}) {
  const invalidate = useInvalidateRecurring();
  const mutation = useMutation({
    mutationFn: () => MUTATION_FN[action](record.id),
    onSettled: () => {
      invalidate(record.id);
      onDone();
    },
  });

  // Fire exactly once when this action component mounts for a fresh request.
  useEffect(() => {
    mutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

// ─────────────────────────────────────────────────────────────────
// Cancel (destructive + irreversible confirm).
// ─────────────────────────────────────────────────────────────────

function RecurringCancelDialog({
  record,
  onClose,
}: {
  record: RecurringDonation;
  onClose: () => void;
}) {
  const invalidate = useInvalidateRecurring();
  const mutation = useMutation({
    mutationFn: () => recurringDonationsApi.cancelRecurringDonation(record.id),
    onSuccess: () => {
      invalidate(record.id);
      onClose();
    },
  });

  return (
    <FormDialog
      open
      onClose={onClose}
      title="Cancel this recurring donation?"
      description={`This stops all future charges for ${donorLabel(record)}. Cancellation cannot be undone — the donor would need to set up a new recurring gift.`}
      fields={[]}
      values={{}}
      onChange={() => {}}
      onSubmit={async () => {
        await mutation.mutateAsync();
      }}
      submitLabel="Cancel recurring donation"
      busy={mutation.isPending}
      error={mutation.isError ? "Could not cancel the recurring donation. Please try again." : undefined}
    />
  );
}
